import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Banner, Box, ButtonAlpha, StackBlock, TextAlpha, TextField, Checkbox, LinkButton, IconButton, } from '@yonderland/andes-react';
import * as Icons from '@yonderland/andes-icons';
import { AuthFormLocation } from '@as-react/common-types';
import { useAemConfig } from '@as-react/common-components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormField } from '../../../form/FormField';
import { I18N_NAMESPACES } from '../../../../constants/i18nConstants';
import { getLoginError } from '../../../../util/LoginUtil';
import { GuestLoginBlock } from './GuestLoginBlock';
import { useCheckoutConfig, useCheckoutState } from '../../../../context/CheckoutContext';
import { useGuestLogin } from '../../../../hooks/useGuestLogin';
import { useCheckoutLogin } from '../../../../hooks/useCheckoutLogin';
export const LoginForm = ({ onEditEmailClick, onForgotPasswordClick }) => {
    const { t } = useTranslation([
        I18N_NAMESPACES.account,
        I18N_NAMESPACES.checkout,
        I18N_NAMESPACES.general,
        I18N_NAMESPACES.navigation,
    ]);
    const { mainWebShop } = useAemConfig();
    const { disableGuestLoginForRegisteredUser } = useCheckoutConfig();
    const [showPassword, setShowPassword] = useState(false);
    const [isLoginSubmitting, setIsLoginSubmitting] = useState(false);
    const [state] = useCheckoutState();
    const { email } = state.login;
    const validationSchema = Yup.object({
        password: Yup.string().required(t(`${I18N_NAMESPACES.account}:delivery.required`)),
        keepSignedIn: Yup.boolean().required(),
    });
    const formProps = useForm({
        defaultValues: { keepSignedIn: false, password: '' },
        mode: 'onTouched',
        resolver: yupResolver(validationSchema),
    });
    const { guestLogin, errorMessage: guestErrorMessage, isPending: isGuestLoginPending } = useGuestLogin();
    const { checkoutLogin, errorMessage: checkoutErrorMessage } = useCheckoutLogin();
    const guestError = t(guestErrorMessage || '');
    const loginError = getLoginError(checkoutErrorMessage || '', t, mainWebShop);
    const handleGuestLoginClick = () => guestLogin(email);
    const onSubmit = values => {
        setIsLoginSubmitting(true);
        checkoutLogin({
            username: email,
            password: values.password,
            keepSignedIn: values.keepSignedIn,
            location: AuthFormLocation.CHECKOUT_V2,
        });
    };
    useEffect(() => {
        Boolean(checkoutErrorMessage) && setIsLoginSubmitting(false);
    }, [checkoutErrorMessage]);
    return (_jsx(FormProvider, Object.assign({}, formProps, { children: _jsx("form", { onSubmit: formProps.handleSubmit(onSubmit), name: "login-form", children: _jsxs(StackBlock, { gap: "6", children: [_jsxs("div", { children: [_jsx(TextAlpha, { variant: "headingS", color: "emphasised", children: t(`${I18N_NAMESPACES.checkout}:checkout.login.title`) }), _jsx(Box, { paddingBlockStart: "3", children: _jsx(TextAlpha, { children: t([
                                        `${I18N_NAMESPACES.checkout}:checkout.login.intro.${mainWebShop}`,
                                        `${I18N_NAMESPACES.checkout}:checkout.login.intro`,
                                    ]) }) })] }), (loginError || guestError) && _jsx(Banner, { text: loginError || guestError, status: "error" }), _jsxs(TextAlpha, { strong: true, wordBreak: "break", children: [email, ' - ', _jsx(LinkButton, { text: t(`${I18N_NAMESPACES.checkout}:checkout.login.edit`), onClick: onEditEmailClick })] }), _jsx(FormField, { name: "password", required: true, children: (_a) => {
                            var _b = _a.fieldProps, { ref } = _b, options = __rest(_b, ["ref"]), { error } = _a;
                            return (_jsx(TextField, Object.assign({}, options, { label: t(`${I18N_NAMESPACES.checkout}:checkout.login.password.label`), name: "password", id: "password", autoComplete: "current-password", error: error, type: showPassword ? 'text' : 'password', labelAdornment: {
                                    text: t(`${I18N_NAMESPACES.checkout}:checkout.login.password_forgotten.label`),
                                    onClick: onForgotPasswordClick,
                                }, testId: "login-form-password", adornmentEnd: _jsx(IconButton, { size: "sm", variant: "plain", onClick: () => setShowPassword(prev => !prev), icon: { source: showPassword ? Icons.EyeFillIcon : Icons.EyeSlashFillIcon, color: 'subtle' } }) })));
                        } }), _jsx(FormField, { name: "keepSignedIn", children: ({ fieldProps }) => (_jsx(Checkbox, Object.assign({}, fieldProps, { checked: fieldProps.value, label: t(`${I18N_NAMESPACES.checkout}:checkout.login.keep_signed_in.label`), name: "keepSignedIn", id: "keepSignedIn", testId: "login-form-keep-signed-in", size: "xs" }))) }), _jsx(ButtonAlpha, { type: "submit", theme: "branded", state: isLoginSubmitting ? 'loading' : 'interactive', text: t(`${I18N_NAMESPACES.checkout}:checkout.login.action`), fullWidth: true, testId: "login-form-submit" }), !disableGuestLoginForRegisteredUser && (_jsx(GuestLoginBlock, { isLoading: isGuestLoginPending, onGuestLoginClick: handleGuestLoginClick }))] }) }) })));
};
